import {
	OIDCTokensTableEntry,
	OIDCTokensResponse,
	OIDCTokensTableKey,
	OIDCEndpointsTableKey,
} from './TypeDefinitions';
import { AuthStorageWrapper } from '../AuthStorageWrapper';
import { storageConstants } from '../AuthConstants';
import { logConstants } from '../../utils/Constants';
import ctxsNative from '../ctxsNative';
import { Logger } from '../Logger';
import { EventType, OIDCTokensUpdatedEvent } from '../Events/EventTypes';
import { v4 } from 'uuid';
import { environment } from '../environment';
import OIDCEndpointsManager from './OIDCEndpointsManager';

export type OIDCTokensUpdateReason = 'login' | 'refresh' | 'native';

/**
 * Singleton class that manages Access and Refresh tokens for OIDC Authentication and also handles operations on
 * the `OIDCTokens` table in IndexedDB.
 */
export default class OIDCTokenManager {
	private static instance: OIDCTokenManager;
	static getInstance(): OIDCTokenManager {
		if (!OIDCTokenManager.instance) {
			OIDCTokenManager.instance = new OIDCTokenManager();
		}
		return this.instance;
	}

	//#region public functions

	/**
	 * Retrieves and decrypts the access and refresh tokens corresponding to the given key from IndexedDB.
	 *
	 * @param {OIDCTokensTableKey} key object of type `OIDCTokensTableKey` containing the authDomain and referenceUri.
	 * @returns {Promise<OIDCTokensTableEntry | null>} object of type `OIDCTokensTableEntry` if the entry was found in the table, `null` otherwise.
	 * @memberof OIDCTokenManager
	 */
	async retrieveOIDCTokens(
		key: OIDCTokensTableKey,
		decryptTokens = true
	): Promise<OIDCTokensTableEntry | null> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		const oidcTokens = (await authStorage.readEntry(storageConstants.OIDC_TOKENS_TABLE, [
			key.authDomain,
			key.referenceUri,
		])) as OIDCTokensTableEntry;
		if (oidcTokens) {
			return decryptTokens ? await this.decryptOIDCTokens(oidcTokens) : oidcTokens;
		}
		return null;
	}

	/**
	 * Encrypts and stores the access and refresh tokens along with the expiry time in the `OIDCTokens` table in IndexedDB.
	 *
	 * @param {OIDCTokensTableKey} key object of type `OIDCTokensTableKey` containing authDomain and referenceUri.
	 * @param {OIDCTokensResponse} tokens object of type `OIDCTokensResponse` containing the response obtained after hitting the token endpoint.
	 * @memberof OIDCTokenManager
	 */
	async saveOIDCTokens(
		key: OIDCTokensTableKey,
		tokens: OIDCTokensResponse,
		serviceURL: string,
		scopes: string,
		reason: OIDCTokensUpdateReason,
		encryptTokens = true
	): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		const oidcTokens = encryptTokens
			? await this.encryptOIDCTokens(tokens, key.referenceUri)
			: tokens;
		let tableEntry: OIDCTokensTableEntry = {
			...key,
			...oidcTokens,
			serviceURL,
		};

		if (!!scopes) {
			tableEntry = { ...tableEntry, scopes };
		}

		Logger.getInstance().write(
			logConstants.INFO,
			'Storing OIDC tokens for reference URI = ' +
				key.referenceUri +
				', auth domain = ' +
				key.authDomain
		);
		await authStorage.updateEntry(storageConstants.OIDC_TOKENS_TABLE, tableEntry);
		await this.sendOIDCTokenUpdateEventToNative(key, serviceURL, tableEntry, reason);
	}

	private async sendOIDCTokenUpdateEventToNative(
		key: OIDCTokensTableKey,
		serviceURL: string,
		tableEntry: OIDCTokensTableEntry,
		reason: OIDCTokensUpdateReason
	) {
		try {
			const { authDomain, referenceUri } = key;
			const oidcEndPointTableKey: OIDCEndpointsTableKey = {
				serviceURL,
				authDomain,
				referenceUri,
			};
			const { revocationEndpoint } =
				(await OIDCEndpointsManager.getInstance().retrieveOIDCEndpoints(
					oidcEndPointTableKey
				)) || {};
			const oidcTokensUpdatedEvent: OIDCTokensUpdatedEvent = {
				id: v4(),
				type: EventType.UPDATED_OIDC_TOKENS,
				payload: {
					tokens: [{ ...tableEntry, revocationEndpoint }],
				},
				meta: {
					reason,
				},
			};
			environment.sendEventToNative(oidcTokensUpdatedEvent);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Error occured while sending oidc token update event: ${e.message}`
			);
		}
	}

	/**
	 * Deletes the access and refresh tokens corresponding to the given key.
	 *
	 * @param {OIDCTokensTableKey} key object of type `OIDCTokensTableKey`
	 * @returns {Promise<void>}
	 * @memberof OIDCTokenManager
	 */
	async deleteOIDCTokens(key: OIDCTokensTableKey): Promise<void> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		Logger.getInstance().write(
			logConstants.INFO,
			'Deleting OIDC tokens for reference URI = ' +
				key.referenceUri +
				', auth domain = ' +
				key.authDomain
		);
		await authStorage.deleteEntry(storageConstants.OIDC_TOKENS_TABLE, [
			key.authDomain,
			key.referenceUri,
		]);
	}

	/**Gets all OIDC Token table entries
	 * @returns an array of OIDC Token table entries
	 * @memberof OIDCTokenManager
	 */
	async retrieveAllTokenTableEntries(): Promise<OIDCTokensTableEntry[]> {
		const authStorage = (await AuthStorageWrapper.getInstance()).getAuthStorage();
		return (await authStorage.readAll(
			storageConstants.OIDC_TOKENS_TABLE
		)) as OIDCTokensTableEntry[];
	}
	//#endregion

	//#region private functions

	/**
	 * Encrypts the access and refresh tokens.
	 */
	private async encryptOIDCTokens(
		tokens: OIDCTokensResponse,
		referenceUri: string
	): Promise<OIDCTokensResponse> {
		return {
			...tokens,
			accessToken: await ctxsNative.Encrypt(referenceUri, tokens.accessToken),
			refreshToken: await ctxsNative.Encrypt(referenceUri, tokens.refreshToken),
		};
	}

	/**
	 * Decrypts the OIDC tokens retrieved from IndexedDB.
	 */
	private async decryptOIDCTokens(
		tokens: OIDCTokensTableEntry
	): Promise<OIDCTokensTableEntry> {
		return {
			...tokens,
			accessToken: await ctxsNative.Decrypt(tokens.referenceUri, tokens.accessToken),
			refreshToken: await ctxsNative.Decrypt(tokens.referenceUri, tokens.refreshToken),
		};
	}

	//TODO: Check if access token has expired

	//#endregion
}
